import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { X, Check, MapPin, Search } from "lucide-react";
import "leaflet/dist/leaflet.css";

// Fix default marker icon (Leaflet CDN paths are broken in bundlers)
const goldIcon = new L.DivIcon({
    className: "custom-loc-pin",
    html: '<div style="background:#B08A1F;color:#fff;width:36px;height:36px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 4px 12px rgba(0,0,0,.35);"><div style="transform:rotate(45deg);font-size:11px;font-weight:700;">📍</div></div>',
    iconSize: [36, 36],
    iconAnchor: [18, 34],
});

const PRESET = {
    Mecca: { lat: 21.4225, lng: 39.8262, zoom: 15, label: "Mekke Merkez (Kâbe)" },
    Medina: { lat: 24.4672, lng: 39.6111, zoom: 15, label: "Medine Merkez (Mescid-i Nebevi)" },
};

const ClickHandler = ({ onPick }) => {
    useMapEvents({
        click(e) {
            onPick(Number(e.latlng.lat.toFixed(6)), Number(e.latlng.lng.toFixed(6)));
        },
    });
    return null;
};

const Recenter = ({ lat, lng }) => {
    const map = useMap();
    useEffect(() => {
        if (lat != null && lng != null) map.setView([lat, lng], map.getZoom());
    }, [lat, lng, map]);
    return null;
};

const LocationPicker = ({ open, city = "Mecca", initialLat, initialLng, onClose, onSelect }) => {
    const preset = PRESET[city] || PRESET.Mecca;
    const [lat, setLat] = useState(initialLat ?? preset.lat);
    const [lng, setLng] = useState(initialLng ?? preset.lng);
    const [search, setSearch] = useState("");
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState([]);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (open) {
            setLat(initialLat ?? preset.lat);
            setLng(initialLng ?? preset.lng);
            setSearch("");
            setResults([]);
            setNotFound(false);
        }
    }, [open, initialLat, initialLng, preset.lat, preset.lng]);

    const runSearch = async () => {
        const q = search.trim();
        if (!q) return;
        setSearching(true);
        setNotFound(false);
        setResults([]);
        try {
            const url = `https://nominatim.openstreetmap.org/search?format=json&limit=5&countrycodes=sa&q=${encodeURIComponent(q + " Saudi Arabia")}`;
            const res = await fetch(url, { headers: { "Accept-Language": "tr,en" } });
            const data = await res.json();
            if (Array.isArray(data) && data.length > 0) {
                setResults(data);
                setLat(Number(Number(data[0].lat).toFixed(6)));
                setLng(Number(Number(data[0].lon).toFixed(6)));
            } else {
                setNotFound(true);
            }
        } catch { setNotFound(true); }
        finally { setSearching(false); }
    };

    const pickResult = (r) => {
        setLat(Number(Number(r.lat).toFixed(6)));
        setLng(Number(Number(r.lon).toFixed(6)));
        setResults([]);
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="loc-picker-modal">
            <div className="bg-emerald_surface border border-gold/50 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border_green">
                    <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gold" />
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-gold">Konum Seç</div>
                            <div className="text-[10px] text-muted_green">{preset.label} çevresi · Haritaya tıkla veya adres ara</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-muted_green hover:text-bone" data-testid="loc-picker-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-3 border-b border-border_green">
                    <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-gold flex-shrink-0" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), runSearch())}
                            placeholder="Otel adı, mahalle veya adres ara (örn: Ajyad, Mekke)"
                            className="input-dark flex-1"
                            data-testid="loc-picker-search"
                        />
                        <button onClick={runSearch} disabled={searching} className="btn-outline-gold !text-xs" data-testid="loc-picker-search-btn">
                            {searching ? "Aranıyor…" : "Ara"}
                        </button>
                    </div>
                    {notFound && (
                        <div className="mt-2 text-xs text-red-400" data-testid="loc-picker-not-found">
                            Sonuç bulunamadı. Farklı bir arama deneyin veya haritaya tıklayarak konumu işaretleyin.
                        </div>
                    )}
                    {results.length > 1 && (
                        <div className="mt-2 border border-border_green divide-y divide-border_green max-h-36 overflow-y-auto" data-testid="loc-picker-results">
                            {results.map((r, i) => (
                                <button
                                    key={r.place_id || i}
                                    onClick={() => pickResult(r)}
                                    className="w-full text-left px-3 py-2 text-xs text-bone/80 hover:bg-emerald_hover hover:text-gold transition-colors flex items-start gap-2"
                                    data-testid={`loc-picker-result-${i}`}
                                >
                                    <MapPin className="w-3 h-3 text-gold flex-shrink-0 mt-0.5" />
                                    <span className="line-clamp-2">{r.display_name}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="relative flex-1 min-h-[420px]">
                    <MapContainer center={[lat, lng]} zoom={preset.zoom} style={{ height: "100%", width: "100%", minHeight: 420 }} scrollWheelZoom>
                        <TileLayer
                            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                            attribution="Tiles &copy; Esri"
                        />
                        <ClickHandler onPick={(la, ln) => { setLat(la); setLng(ln); }} />
                        <Recenter lat={lat} lng={lng} />
                        <Marker position={[lat, lng]} icon={goldIcon} />
                    </MapContainer>
                    <div className="absolute bottom-2 left-2 right-2 bg-emerald_deep/85 backdrop-blur border border-gold/40 px-3 py-2 text-xs flex items-center gap-3 flex-wrap" data-testid="loc-picker-coords">
                        <span className="text-muted_green">Enlem:</span> <span className="font-mono text-gold">{lat.toFixed(6)}</span>
                        <span className="text-muted_green">Boylam:</span> <span className="font-mono text-gold">{lng.toFixed(6)}</span>
                    </div>
                </div>

                <div className="p-3 border-t border-border_green flex items-center justify-between gap-3">
                    <div className="text-[11px] text-muted_green">Haritaya tıklayarak konumu değiştirebilirsiniz.</div>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-muted_green hover:text-bone px-3 py-2">
                            İptal
                        </button>
                        <button
                            onClick={() => { onSelect(lat, lng); onClose(); }}
                            className="btn-gold !text-xs"
                            data-testid="loc-picker-confirm"
                        >
                            <Check className="w-3.5 h-3.5" /> Bu Konumu Kullan
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LocationPicker;
